/**
 * Deterministic Per-Action Cost Calculator
 *
 * Pure functions that return the exact cost in USD cents for each billable
 * action. Used by the backend with live AdminSettings from DynamoDB, and
 * by the frontend with ADMIN_SETTINGS_DEFAULTS for client-side pre-checks.
 *
 * All constants include a 2x margin over AWS cost, baked in. There is no
 * separate margin parameter — the returned cost IS the final user-facing price.
 *
 * ## Per-pipeline cost shape
 *
 * Each pipeline makes a different number of Bedrock calls. The fixed cost
 * is calibrated separately per pipeline so the 2x margin holds in the
 * worst case. The variable per-word cost is identical across all pipelines
 * (output tokens, Translate, and Polly scale with the article body, not
 * the pipeline shape).
 *
 * ### Generated pipeline (3 Bedrock text + 1 Nova Canvas + 1 phrase-shred Bedrock)
 *   Fixed: 11 cents (description + image prompt + Nova + 1 sub-phrase Bedrock, × 2)
 *
 * ### Imported pipeline (1-6 Bedrock text + 1 Nova Canvas + 1 phrase-shred Bedrock)
 *   Fixed: 14 cents (cleanup + CEFR + summarise + description + image + sub-phrase, × 2)
 *
 * ## Variable cost (per output word, both pipelines)
 *   Bedrock text generation:        1.5 tok/word × $15/M output = $0.0000225/word
 *   AWS Translate (chunk-level):    5 chars/word × $15/M chars  = $0.0000750/word
 *   AWS Translate (phrase shred):   5 chars/word × $15/M chars  = $0.0000750/word
 *   AWS Polly × 2 calls:            5 chars/word × 2 × $4/M     = $0.0000400/word
 *   Raw total:                      $0.0002125/word → at 2x = $0.000425/word
 *   In microcents (1/10000 cent): basePerWord 345 + audioPerWord 80 = 425
 *
 * ## Translation costs
 *   Fixed 2¢ per custom (interactive) translation regardless of length.
 *
 * ## Verification
 *   300-word generated article: 11 + ceil(300 × 425 / 10000) = 11 + 13 = 24 cents
 *   300-word imported article:  14 + ceil(300 × 425 / 10000) = 14 + 13 = 27 cents
 */

import { AdminSettings, ADMIN_SETTINGS_DEFAULTS } from '../entities/admin-settings-entity';

/** The subset of AdminSettings needed by the cost functions. */
export type PricingSettings = Pick<AdminSettings,
  | 'articleFixedCostCents'
  | 'importFixedCostCents'
  | 'basePerWordMicrocents'
  | 'audioPerWordMicrocents'
  | 'translationFixedCostCents'
>;

function perWordMicrocents(settings: PricingSettings): number {
  return settings.basePerWordMicrocents + settings.audioPerWordMicrocents;
}

/**
 * Calculate the cost of generating an article.
 */
export function calculateArticleCost(
  wordCount: number,
  settings: PricingSettings = ADMIN_SETTINGS_DEFAULTS,
): { costCents: number } {
  const variableCents = Math.ceil(wordCount * perWordMicrocents(settings) / 10_000);
  return { costCents: settings.articleFixedCostCents + variableCents };
}

/**
 * Calculate the cost of importing an article from a URL or uploaded document.
 */
export function calculateImportCost(
  targetWordCount: number,
  settings: PricingSettings = ADMIN_SETTINGS_DEFAULTS,
): { costCents: number } {
  const variableCents = Math.ceil(targetWordCount * perWordMicrocents(settings) / 10_000);
  return { costCents: settings.importFixedCostCents + variableCents };
}

/**
 * Calculate the cost of a custom translation (interactive phrase translation).
 * Fixed price per translation regardless of length.
 */
export function calculateTranslationCost(
  _charCount: number,
  settings: PricingSettings = ADMIN_SETTINGS_DEFAULTS,
): { costCents: number } {
  return { costCents: settings.translationFixedCostCents };
}
